/**
 * usePremiumTriggerAnalytics — Tracks paywall trigger impressions and clicks.
 *
 * Spec SOS-332 rule 5:
 *   - `premium_trigger_shown`    {origin: 'settings' | 'roll_review_block' | 'onboarding', subscription_state}
 *   - `premium_trigger_clicked`  {origin, subscription_state}
 *
 * Provider-agnostic: today we just emit a `dispatchEvent('sossiety:analytics', detail)`
 * which the host app can wire to PostHog/Mixpanel/etc. When a real provider is
 * picked, replace `dispatch()` here only — no caller change required.
 *
 * Why a hook (not a service): components need a stable callback identity, and
 * we lift the `origin` parameter so the hook does the tagging exactly once.
 */

import { useCallback } from 'react';
import { useSubscriptionStatus } from './useSubscriptionStatus';
import { ANALYTICS_BUS_EVENT } from '@/integrations/analytics/events';

export type PremiumTriggerOrigin = 'settings' | 'roll_review_block' | 'onboarding';

interface AnalyticsPayload {
  origin: PremiumTriggerOrigin;
  subscription_state: string;
}

/**
 * Dispatches the event so any global listener (e.g. an `analytics-bridge.ts`)
 * can forward to the configured tracking provider.
 */
type PremiumAnalyticsName =
  | 'premium_trigger_shown'
  | 'premium_trigger_clicked'
  | 'paywall_dismissed';

function dispatch(name: PremiumAnalyticsName, detail: AnalyticsPayload) {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(ANALYTICS_BUS_EVENT, { detail: { name, ...detail } }));
    // Dev-time visibility — silent in prod.
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.debug('[analytics]', name, detail);
    }
  } catch {
    /* never throw from analytics */
  }
}

export interface PremiumTriggerAnalytics {
  trackShown: () => void;
  trackClicked: () => void;
  trackDismissed: () => void;
}

export function usePremiumTriggerAnalytics(origin: PremiumTriggerOrigin): PremiumTriggerAnalytics {
  const { displayState } = useSubscriptionStatus();
  const state = displayState ?? 'unknown';

  const trackShown = useCallback(
    () => dispatch('premium_trigger_shown', { origin, subscription_state: state }),
    [origin, state],
  );

  const trackClicked = useCallback(
    () => dispatch('premium_trigger_clicked', { origin, subscription_state: state }),
    [origin, state],
  );

  const trackDismissed = useCallback(
    () => dispatch('paywall_dismissed', { origin, subscription_state: state }),
    [origin, state],
  );

  return { trackShown, trackClicked, trackDismissed };
}
